/**
 * ContinueWatchingSection
 * Shows videos that the user has started watching but not finished
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, Clock, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { getContinueWatching, deleteWatchHistoryEntry } from '../services/watchHistory';

export const ContinueWatchingSection = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('userToken');
    setIsLoggedIn(!!token);
    
    if (token) {
      fetchContinueWatching();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchContinueWatching = async () => {
    const data = await getContinueWatching(6);
    setVideos(data);
    setLoading(false);
  };

  const handleRemove = async (contentId, e) => {
    e.preventDefault();
    e.stopPropagation();
    await deleteWatchHistoryEntry(contentId);
    setVideos(videos.filter(v => v.content_id !== contentId));
  };

  const formatProgress = (progress, duration) => {
    if (!duration) return '0%';
    const percent = Math.round((progress / duration) * 100);
    return `${percent}%`;
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Don't show if user is not logged in or no videos
  if (!isLoggedIn || loading || videos.length === 0) {
    return null;
  }

  return (
    <section className="py-8 bg-black" data-testid="continue-watching-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white">
            Continue Watching
          </h2>
          <Link 
            to="/account" 
            className="text-sm text-zinc-400 hover:text-white transition-colors"
          >
            View History →
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {videos.map((video, index) => (
            <motion.div
              key={video.content_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={video.content_type === 'recording' 
                  ? `/on-demand/${video.content_id}` 
                  : `/watch/${video.channel_id || 'channel1'}`}
                className="block group relative"
                data-testid={`continue-watching-${video.content_id}`}
              >
                {/* Thumbnail */}
                <div className="relative aspect-video bg-zinc-900 overflow-hidden">
                  {video.thumbnail ? (
                    <img 
                      src={video.thumbnail} 
                      alt={video.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Play className="w-10 h-10 text-zinc-700" />
                    </div>
                  )}

                  {/* Progress bar */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-800">
                    <div 
                      className="h-full bg-orange-500"
                      style={{ 
                        width: formatProgress(video.progress, video.duration)
                      }}
                    />
                  </div>

                  {/* Play overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                      <Play className="w-6 h-6 text-black ml-0.5" />
                    </div>
                  </div>

                  {/* Remove button */}
                  <button
                    onClick={(e) => handleRemove(video.content_id, e)}
                    className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
                    data-testid={`remove-continue-watching-${video.content_id}`}
                  >
                    <X className="w-3.5 h-3.5 text-white" />
                  </button>

                  {/* Time remaining */}
                  <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/80 text-[10px] text-white flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatTime((video.duration || 0) - (video.progress || 0))} left
                  </div>
                </div>

                {/* Title */}
                <div className="mt-2">
                  <h3 className="text-sm font-medium text-white truncate group-hover:text-orange-500 transition-colors">
                    {video.title || 'Untitled Video'}
                  </h3>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {formatProgress(video.progress, video.duration)} watched
                  </p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ContinueWatchingSection;
